import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext';
import { useTheme } from '../../context/ThemeContext';
import { fetchAllProducts } from '../../services/productsApi';
import { API_CONFIG } from '../../services/config';
import type { Product } from '../../types/product';

const CATEGORY_LABELS: Record<string, string> = {
  gpu: 'Tarjeta Gráfica',
  cpu: 'Procesador',
  ram: 'Memoria RAM',
  storage: 'Almacenamiento',
  chairs: 'Silla Gaming',
  monitors: 'Monitor',
  accessories: 'Accesorio',
  builds: 'Ensamblaje',
};

export default function GlobalSearch() {
  const { isOpen, close } = useSearch();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced API search — always hits the real backend
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchAllProducts({ globalFilter: q });
        setSuggestions(results.slice(0, 8));
        setHighlighted(-1);
      } catch (err) {
        setError('No se pudieron cargar los productos');
        console.error('[GlobalSearch]', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Focus input when opening
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSuggestions([]);
      setHighlighted(-1);
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Close on Escape or outside click
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        close();
      }
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      document.body.style.overflow = '';
    };
  }, [isOpen, close]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (suggestions.length === 0) {
        if (e.key === 'Enter' && query.trim().length >= 2) {
          e.preventDefault();
          close();
          navigate(`/products?search=${encodeURIComponent(query.trim())}`);
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlighted >= 0) {
          const product = suggestions[highlighted];
          close();
          navigate(`/products/${API_CONFIG.COMPANY_ID}/${product.id}`);
        } else if (query.trim().length >= 2) {
          close();
          navigate(`/products?search=${encodeURIComponent(query.trim())}`);
        }
      } else if (e.key === 'Escape') {
        close();
      }
    },
    [suggestions, highlighted, query, close, navigate]
  );

  const handleSelect = useCallback(
    (product: Product) => {
      close();
      navigate(`/products/${API_CONFIG.COMPANY_ID}/${product.id}`);
    },
    [close, navigate]
  );

  const handleViewAll = useCallback(() => {
    if (!query.trim()) return;
    close();
    navigate(`/products?search=${encodeURIComponent(query.trim())}`);
  }, [query, close, navigate]);

  if (!isOpen) return null;

  const bgOverlay = isDark ? 'bg-black/70' : 'bg-black/40';
  const bgPanel = isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200';
  const textPrimary = isDark ? 'text-white' : 'text-zinc-900';
  const textSecondary = isDark ? 'text-zinc-400' : 'text-zinc-500';
  const textMuted = isDark ? 'text-zinc-500' : 'text-zinc-400';
  const inputBg = isDark ? 'bg-zinc-800/60 border-zinc-700' : 'bg-white border-zinc-200';
  const hoverBg = isDark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-50';
  const activeBg = isDark ? 'bg-zinc-800' : 'bg-zinc-50';

  return (
    <div className={`fixed inset-0 z-[60] ${bgOverlay} backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4`}>
      <div
        ref={wrapperRef}
        className={`w-full max-w-2xl ${bgPanel} border rounded-2xl shadow-2xl overflow-hidden`}
      >
        {/* Input */}
        <div className="relative px-4 pt-4 pb-3">
          <div className="absolute left-8 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-search-line text-zinc-400 text-base" />
          </div>
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar productos por nombre, SKU, marca..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlighted(-1);
            }}
            onKeyDown={handleKeyDown}
            className={`w-full ${inputBg} border text-sm rounded-xl pl-11 pr-10 py-3.5 focus:outline-none focus:border-red-400 transition-colors ${textPrimary} placeholder-zinc-400`}
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setHighlighted(-1); inputRef.current?.focus(); }}
              className="absolute right-8 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-zinc-400 text-xs" />
            </button>
          )}
        </div>

        {/* Suggestions */}
        {loading && (
          <div className="px-4 py-6 text-center">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className={`text-xs ${textMuted}`}>Cargando productos...</p>
          </div>
        )}

        {error && !loading && (
          <div className="px-4 py-6 text-center">
            <i className="ri-error-warning-line text-red-500 text-xl mb-2" />
            <p className="text-xs text-red-500">{error}</p>
          </div>
        )}

        {!loading && !error && suggestions.length > 0 && (
          <div className="max-h-[50vh] overflow-y-auto">
            {suggestions.map((product, i) => {
              const imgSrc = product.images?.[0];
              const hasImg = imgSrc && imgSrc.trim().length > 0;
              return (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product)}
                  onMouseEnter={() => setHighlighted(i)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer border-b last:border-0 ${
                    isDark ? 'border-zinc-800' : 'border-zinc-100'
                  } ${highlighted === i ? activeBg : hoverBg}`}
                >
                  <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-white flex items-center justify-center">
                    {hasImg ? (
                      <img
                        src={imgSrc}
                        alt={product.name}
                        className="w-full h-full object-contain p-0.5"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          const parent = (e.target as HTMLImageElement).parentElement;
                          if (parent) {
                            parent.innerHTML = '<i class="ri-image-line text-lg text-zinc-200"></i>';
                          }
                        }}
                      />
                    ) : (
                      <i className="ri-image-line text-lg text-zinc-200" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${textPrimary}`}>{product.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] ${textMuted}`}>
                        {CATEGORY_LABELS[product.category] ?? product.productSubCategory ?? product.category ?? 'Producto'}
                      </span>
                      {product.price != null && product.price > 0 && (
                        <>
                          <span className={isDark ? 'text-zinc-700' : 'text-zinc-200'}>·</span>
                          <span className="text-red-500 text-[10px] font-bold">
                            RD${product.price.toLocaleString('es-DO')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <i className="ri-arrow-right-up-line text-zinc-300 text-sm flex-shrink-0" />
                </button>
              );
            })}

            {/* View all link */}
            <button
              onClick={handleViewAll}
              className={`w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors cursor-pointer border-t ${
                isDark ? 'border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800' : 'border-zinc-100 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50'
              }`}
            >
              <i className="ri-layout-grid-line" />
              Ver todos los resultados en el catálogo
            </button>
          </div>
        )}

        {!loading && !error && query.trim().length >= 2 && suggestions.length === 0 && (
          <div className="px-4 py-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-zinc-100">
              <i className="ri-search-line text-zinc-300 text-xl" />
            </div>
            <p className={`text-sm font-medium ${textSecondary}`}>No se encontraron productos</p>
            <p className={`text-xs mt-1 ${textMuted}`}>Intenta con otro término de búsqueda</p>
            <button
              onClick={handleViewAll}
              className="mt-3 text-xs text-red-500 hover:text-red-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Ir al catálogo
            </button>
          </div>
        )}

        {!loading && !error && query.trim().length < 2 && (
          <div className={`px-4 py-4 text-center ${textMuted}`}>
            <p className="text-xs">Escribe al menos 2 caracteres para buscar</p>
            <div className="flex items-center justify-center gap-4 mt-3">
              <span className="text-[10px] bg-zinc-100 px-2 py-1 rounded-md">↑ ↓ navegar</span>
              <span className="text-[10px] bg-zinc-100 px-2 py-1 rounded-md">Enter seleccionar</span>
              <span className="text-[10px] bg-zinc-100 px-2 py-1 rounded-md">Esc cerrar</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
